import { atom } from "jotai";
import { DateTime } from "luxon";
import type { AdminEmissionsRecordsStatsListProviderWithSourceParameterInner } from "../../apiClient/generated";

/**
 * Analytics page filters - controls data fetched on the entire page.
 */
export interface AnalyticsFilters {
    quarterFrom: string; // Format: "2020-Q1"
    quarterTo: string; // Format: "2024-Q1"
    providers?:
        | AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[]
        | "all";
    detectionLimit: number; // kg/h
    locationPreset?: number[]; // Geographic location filter (array of preset IDs)
    siteType?: string[]; // Site type filter (array of site type strings)
}

/**
 * Get current quarter string in format "YYYY-QN"
 */
const getCurrentQuarter = (): string => {
    const now = DateTime.now();
    const quarter = Math.ceil(now.month / 3);
    return `${now.year}-Q${quarter}`;
};

/**
 * Get quarter N periods ago from current quarter
 * @param n Number of quarters ago (e.g., 7 means 8 quarters ago inclusive)
 */
const getQuarterNPeriodsAgo = (n: number): string => {
    const now = DateTime.now();
    const currentQuarter = Math.ceil(now.month / 3);
    const totalQuarters = now.year * 4 + currentQuarter;
    const targetQuarters = totalQuarters - n;
    const year = Math.floor((targetQuarters - 1) / 4);
    const quarter = ((targetQuarters - 1) % 4) + 1;
    return `${year}-Q${quarter}`;
};

/**
 * Default analytics filters
 * Default: last 8 quarters (7 periods ago to current)
 */
const getDefaultFilters = (): AnalyticsFilters => ({
    quarterFrom: getQuarterNPeriodsAgo(7), // 8 quarters ago (inclusive)
    quarterTo: getCurrentQuarter(),
    providers: undefined, // All providers
    detectionLimit: 250, // Default 250 kg/h
    locationPreset: undefined, // No location filter by default
    siteType: undefined, // No site type filter by default
});

export const defaultAnalyticsFilters = getDefaultFilters();

/**
 * Returns true if fitlers are at defaults.
 *
 * @param filters
 * @returns
 */
export const isDefaultFilters = (filters: AnalyticsFilters): boolean => {
    const defaults = getDefaultFilters();
    return (
        filters.quarterFrom === defaults.quarterFrom &&
        filters.quarterTo === defaults.quarterTo &&
        filters.providers === defaults.providers &&
        filters.detectionLimit === defaults.detectionLimit &&
        filters.locationPreset === defaults.locationPreset &&
        filters.siteType === defaults.siteType
    );
};

/**
 * Analytics filters atom - manages quarter range selection
 * Default: last 8 quarters (7 periods ago to current)
 */
export const analyticsFiltersAtom = atom<AnalyticsFilters>(getDefaultFilters());
